// Distribution — two independent decisions, matching planner sheets 2 & 3.
//
// Card 1 "Where to send new production" (sheet 2, corrected): take ONLY a run's freshly-produced
// cans and split each SKU 100% across channels by that SKU's own channel velocity (packs sold/mo).
// Nothing is drawn from hub stock. Output = mix-pack vs single-flavour pallets for TikTok/Amazon;
// the hub (Shopify) keeps the remainder.
//
// Card 2 "Continuous rebalance" (sheet 3): per SKU × TikTok/Amazon, days-of-cover = inv ÷ daily
// sales; restock when cover < lead; ship to (lead + send) days. Each SKU (including the Mix Pack) is
// independent — a single-flavour restock covers single-flavour demand only. The mixCansPerFlavour
// setting is a production-planning constant (assembling mixes), deliberately NOT applied here.
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import type { ChannelsMatrix } from "../lib/api.ts";
import { channelRestock, pallets, splitByChannelVelocity } from "@shared/engine.ts";
import { api } from "../lib/api.ts";
import { useData } from "../state/store.tsx";
import { Badge, EmptyState, InfoTip, Modal, SkeletonCard } from "../components/ui.tsx";
import { num } from "../lib/format.ts";

export default function Distribution() {
  const { data, mutate, saving } = useData();
  const bizId = data?.business.id || "";
  const [cm, setCm] = useState<ChannelsMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [runId, setRunId] = useState("");
  const [amzBusy, setAmzBusy] = useState(false);
  const [amzMsg, setAmzMsg] = useState<string | null>(null);
  const [amzDetail, setAmzDetail] = useState<{ sellerSku: string; inv: number; monthly: number; mapped?: boolean }[] | null>(null);
  const [editSku, setEditSku] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!bizId) return;
    setLoading(true);
    try { setCm(await api.channels(bizId)); } finally { setLoading(false); }
  }, [bizId]);
  useEffect(() => { void load(); }, [load]);

  const syncAmazon = useCallback(async () => {
    setAmzBusy(true); setAmzMsg("Requesting Amazon reports…");
    try {
      let res = await api.amazon(bizId, true);
      if (!res.configured) { setAmzMsg("Amazon not configured."); return; }
      for (let i = 0; i < 12 && !res.ready; i++) { await new Promise((r) => setTimeout(r, 8000)); res = await api.amazon(bizId); if (res.error) { setAmzMsg(`Amazon: ${res.error}`); return; } }
      if (res.ready) { setAmzMsg(res.note ? `Amazon synced (${res.skus ?? 0} SKUs with sales) — ${res.note}` : `Amazon synced (${res.skus ?? 0} SKUs)`); setAmzDetail(res.detail ?? null); await load(); } else setAmzMsg("Amazon still processing — try again shortly.");
    } finally { setAmzBusy(false); }
  }, [bizId, load]);

  const dist = data?.business.distribution || { rebalanceLeadDays: 21, daysToSendDays: 30, packsPerPallet: 192 };
  const pp = dist.packsPerPallet || 192;
  const defaultPack = dist.defaultPackSize || 12;

  const runItems = useMemo(() => {
    if (!data || !runId) return [] as { skuId: string; cans: number }[];
    const run = data.runs.find((r) => r.id === runId);
    if (!run) return [];
    const planned = run.items.reduce((a, i) => a + i.cans, 0);
    const scale = run.actualYield && planned > 0 ? run.actualYield / planned : 1;
    return run.items.map((i) => ({ skuId: i.skuId, cans: Math.round(i.cans * scale) })).filter((i) => i.cans > 0);
  }, [data, runId]);

  const channels = useMemo(() => cm?.channels || [], [cm]);
  const skus = useMemo(() => cm?.skus || [], [cm]);
  const cell = useCallback(
    (skuId: string, chId: string) => cm?.matrix[skuId]?.[chId] || { invPacks: 0, packsMo: 0, source: "manual" as const, pending: false },
    [cm],
  );

  const hubCh = useMemo(() => channels.find((c) => c.kind === "shopify") || channels[0], [channels]);
  const hubId = hubCh?.id || "";
  const nonHub = useMemo(() => channels.filter((c) => c.id !== hubId), [channels, hubId]);
  const hubName = hubCh?.name || "the hub";
  const shipTargets = nonHub.map((c) => c.name).join(" & "); // e.g. "TikTok & Amazon" (empty for Shopify-only markets)
  const mixSku = useMemo(() => skus.find((s) => s.assembled), [skus]);
  const packOf = useCallback((s: { packSize?: number }) => Number(s.packSize) || defaultPack, [defaultPack]);

  // ── Card 1: split each produced SKU across channels by its own channel velocity ──
  const allocation = useMemo(() => {
    const out: Record<string, Record<string, number>> = {};
    if (!hubId) return out;
    for (const it of runItems) {
      const vel: Record<string, number> = {};
      for (const c of channels) vel[c.id] = cell(it.skuId, c.id).packsMo;
      out[it.skuId] = splitByChannelVelocity(it.cans, vel, hubId);
    }
    return out;
  }, [runItems, channels, cell, hubId]);

  // SKUs that fell back to hub because they have no recorded velocity anywhere.
  const noVelocitySkus = useMemo(
    () => runItems.filter((it) => channels.every((c) => cell(it.skuId, c.id).packsMo <= 0)).map((it) => it.skuId),
    [runItems, channels, cell],
  );
  const anyPending = useMemo(() => skus.some((s) => channels.some((c) => cell(s.skuId, c.id).pending)), [skus, channels, cell]);

  if (!data) return null;

  // Pack formats a channel sells (manual channels can sell several; synced sell only their base pack).
  const bizChannels = data.business.channels || [];
  const chFormats = (chId: string): number[] => {
    const bc = bizChannels.find((x) => x.id === chId);
    const ps = (bc?.packSizes || []).filter((n) => n > 0);
    return ps.length ? ps : [defaultPack];
  };
  const vKey = (skuId: string, size: number) => (size === defaultPack ? skuId : `${skuId}|${size}`);
  const manualVal = (chId: string, key: string) => {
    const m = data.business.channelData?.[chId]?.[key] || {};
    return { inv: Number(m.invPacks) || 0, sold: Number(m.packsMo) || 0 };
  };
  const fmtLabel = (size: number) => (size === 1 ? "single" : `${size}pk`);

  const skuName = (skuId: string) => skus.find((s) => s.skuId === skuId)?.name || skuId;
  const allocCans = (skuId: string, chId: string) => allocation[skuId]?.[chId] || 0;
  // Cans routed to a channel, filtered to mix vs single, expressed in retail packs.
  const packsTo = (chId: string, wantMix: boolean) =>
    runItems.reduce((a, it) => {
      const s = skus.find((x) => x.skuId === it.skuId);
      if (!s || !!s.assembled !== wantMix) return a;
      return a + allocCans(it.skuId, chId) / packOf(s);
    }, 0);

  // ── Card 2: each SKU rebalances independently (matches planner sheet 3 exactly). Mix Pack is its
  // own line; single flavours are NOT diluted by mix stock — restocking a single covers single
  // demand only (a mix pack doesn't satisfy a single-flavour order). The mixCansPerFlavour setting
  // is used in production planning (assembling mixes), not here. ──
  // Manual cells (TikTok, Mix-Pack-on-Shopify) are edited locally in channelData, which updates
  // instantly on save — so read those from `data`, not the (only-on-refresh) fetched matrix.
  const cover = (skuId: string, chId: string) => {
    const base = cell(skuId, chId);
    if (base.source === "manual") {
      const m = data.business.channelData?.[chId]?.[skuId] || {};
      return { inv: Number(m.invPacks) || 0, sold: Number(m.packsMo) || 0 };
    }
    return { inv: base.invPacks, sold: base.packsMo };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold">Distribution</h2>
          <p className="text-muted text-xs mt-0.5">New production is split by velocity; existing stock is rebalanced to cover. Per SKU × channel in {defaultPack}-packs; pallets at {pp}/pallet.</p>
        </div>
        <div className="flex items-center gap-2">
          {channels.some((c) => c.kind === "amazon" && c.auto) && <button className="btn-sm border border-edge hover:bg-paper" disabled={amzBusy} onClick={syncAmazon}>{amzBusy ? "Syncing Amazon…" : "Sync Amazon"}</button>}
          <button className="btn-sm border border-edge hover:bg-paper" disabled={loading} onClick={load}>{loading ? "…" : "Refresh"}</button>
        </div>
      </div>
      {amzMsg && <div className="card border-brand/30 bg-brandSoft text-brand text-xs">{amzMsg}</div>}
      {amzDetail && amzDetail.length > 0 && (
        <details className="card text-xs">
          <summary className="cursor-pointer font-medium">Amazon FBA seller-SKUs ({amzDetail.length}) — map these in Admin → SKUs → Amazon SKU</summary>
          <table className="w-full mt-2 min-w-[360px]">
            <thead><tr><th className="th">FBA seller-SKU</th><th className="th text-right">Inv (units)</th><th className="th text-right">Sold / mo</th><th className="th text-right">Mapped?</th></tr></thead>
            <tbody>
              {amzDetail.map((d) => (
                <tr key={d.sellerSku}>
                  <td className="td font-mono text-[11px]">{d.sellerSku}</td>
                  <td className="td text-right tabular-nums">{num(d.inv)}</td>
                  <td className="td text-right tabular-nums">{num(d.monthly)}</td>
                  <td className="td text-right">{d.mapped ? <span className="text-ok">✓</span> : <span className="text-warn">— add</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-muted mt-2">Copy a code with sales into the matching SKU’s <span className="font-medium">Amazon SKU</span> field so its velocity shows here.</p>
        </details>
      )}
      {anyPending && !amzMsg && <div className="card border-amber-300/50 bg-amber-50 text-amber-800 text-xs">Amazon figures aren’t loaded yet — click <span className="font-medium">Sync Amazon</span> to pull live inventory &amp; velocity.</div>}

      {loading && !cm ? <SkeletonCard lines={6} /> : channels.length === 0 ? (
        <div className="card"><EmptyState title="No channels configured" sub="Add sales channels in Admin → Channels." /></div>
      ) : (
        <>
          {/* ── Card 1: Where to send new production ── */}
          <div className="card overflow-x-auto">
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <h3 className="text-sm font-semibold">Where to send new production <InfoTip text={`Only the cans from the selected run. Each SKU is split 100% across channels in proportion to that SKU's own sales velocity per channel. The DTC hub (${hubName}) keeps its share${shipTargets ? `; ${shipTargets} shares become pallets to ship` : ""}.`} /></h3>
              <label className="text-sm">
                <span className="label">From production run</span>
                <select className="input w-auto py-1.5" value={runId} onChange={(e) => setRunId(e.target.value)}>
                  <option value="">— select a run —</option>
                  {data.runs.filter((r) => ["Delivered", "In Production", "Waiting Pickup", "Ingredients Received"].includes(r.status as string)).map((r) => (
                    <option key={r.id} value={r.id}>{new Date(r.scheduledDate).toLocaleDateString()} · {r.status} · {num(r.actualYield || r.items.reduce((a, i) => a + i.cans, 0))} cans</option>
                  ))}
                </select>
              </label>
            </div>
            {!runId ? <p className="text-muted text-sm py-4">Select a produced run to see how to split it across channels.</p>
              : runItems.length === 0 ? <p className="text-muted text-sm py-4">That run has no produced cans to distribute.</p> : (
              <>
                <table className="w-full min-w-[640px]">
                  <thead><tr>
                    <th className="th">Produced SKU</th>
                    <th className="th text-right">Made</th>
                    {channels.map((c) => <th key={c.id} className="th text-right">{c.name}{c.id === hubId && <span className="text-muted font-normal text-[10px] block">hub · stays</span>}</th>)}
                  </tr></thead>
                  <tbody>
                    {runItems.map((it) => {
                      const s = skus.find((x) => x.skuId === it.skuId);
                      return (
                        <tr key={it.skuId}>
                          <td className="td font-medium">{skuName(it.skuId)}{s?.assembled && <span className="text-muted text-[11px] ml-1">(mix)</span>}</td>
                          <td className="td text-right tabular-nums text-muted">{num(it.cans)}</td>
                          {channels.map((c) => <td key={c.id} className="td text-right tabular-nums">{num(allocCans(it.skuId, c.id))}</td>)}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr>
                    <td className="td font-semibold">Total cans</td>
                    <td className="td text-right font-semibold tabular-nums">{num(runItems.reduce((a, i) => a + i.cans, 0))}</td>
                    {channels.map((c) => <td key={c.id} className="td text-right font-semibold tabular-nums">{num(runItems.reduce((a, it) => a + allocCans(it.skuId, c.id), 0))}</td>)}
                  </tr></tfoot>
                </table>
                {noVelocitySkus.length > 0 && (
                  <p className="text-[11px] text-muted mt-2">No channel velocity recorded for {noVelocitySkus.map(skuName).join(", ")} — routed to {hubCh?.name || "the hub"} in full. Add sales figures to split it.</p>
                )}
                {/* Pallets to ship out (hub keeps its share) */}
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  {nonHub.map((c) => {
                    const mixPk = packsTo(c.id, true);
                    const singlePk = packsTo(c.id, false);
                    return (
                      <div key={c.id} className="rounded-lg border border-edge/70 p-3 text-sm">
                        <div className="font-medium mb-1">{c.name} — pallets to send</div>
                        {mixSku && <div className="flex justify-between"><span className="text-muted">Mix-pack pallets <span className="text-[10px]">({num(Math.round(mixPk))} packs)</span></span><span className="tabular-nums font-semibold">{pallets(mixPk, pp)}</span></div>}
                        <div className="flex justify-between"><span className="text-muted">Single-flavour pallets <span className="text-[10px]">({num(Math.round(singlePk))} packs)</span></span><span className="tabular-nums font-semibold">{pallets(singlePk, pp)}</span></div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* ── Card 2: Continuous rebalance ── */}
          <div className="card overflow-x-auto">
            <h3 className="text-sm font-semibold mb-1">Continuous rebalance <InfoTip text={`Days of cover per channel in ${defaultPack}-packs.${shipTargets ? ` Restock ${shipTargets} when cover drops below the ${dist.rebalanceLeadDays}-day lead time; ship enough for ${dist.rebalanceLeadDays + dist.daysToSendDays} days minus what's already there.` : ""} Each SKU is independent — the Mix Pack has its own line.`} /></h3>
            <p className="text-muted text-xs mb-3">{shipTargets ? <>Restock when cover &lt; lead {dist.rebalanceLeadDays}d · ship to {dist.rebalanceLeadDays + dist.daysToSendDays}d of cover. </> : null}{hubName} is the hub (never restocked).</p>
            <table className="w-full min-w-[760px]">
              <thead><tr>
                <th className="th">SKU</th>
                {channels.map((c) => <th key={c.id} className="th text-right">{c.name}<div className="text-[10px] font-normal text-muted/70">inv · /mo · days</div></th>)}
                {nonHub.map((c) => <th key={`r${c.id}`} className="th text-right">{c.name} restock</th>)}
                <th className="th"></th>
              </tr></thead>
              <tbody>
                {skus.map((s) => (
                  <tr key={s.skuId}>
                    <td className="td font-medium">{s.name}{s.assembled && <span className="text-muted text-[11px] ml-1">(mix)</span>}</td>
                    {channels.map((c) => {
                      const raw = cell(s.skuId, c.id);
                      const editable = raw.source === "manual";
                      if (editable) {
                        const fmts = chFormats(c.id);
                        const multi = fmts.length > 1;
                        return (
                          <td key={c.id} className="td text-right">
                            <div className="space-y-1">
                              {fmts.map((size) => {
                                const val = manualVal(c.id, vKey(s.skuId, size));
                                const rec2 = channelRestock(val.inv, val.sold, dist.rebalanceLeadDays, dist.daysToSendDays);
                                return (
                                  <div key={size} className="tabular-nums text-xs">
                                    {multi && <span className="text-[9px] text-muted mr-1">{fmtLabel(size)}</span>}
                                    {num(Math.round(val.inv))} · {num(Math.round(val.sold))} · {rec2.daysLeft == null ? "—" : `${Math.round(rec2.daysLeft)}d`}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        );
                      }
                      const cv = cover(s.skuId, c.id);
                      const rec = channelRestock(cv.inv, cv.sold, dist.rebalanceLeadDays, dist.daysToSendDays);
                      return (
                        <td key={c.id} className="td text-right">
                          <span className="tabular-nums text-xs">{num(Math.round(cv.inv))} · {num(Math.round(cv.sold))} · {rec.daysLeft == null ? "—" : `${Math.round(rec.daysLeft)}d`}</span>
                        </td>
                      );
                    })}
                    {nonHub.map((c) => {
                      if (cell(s.skuId, c.id).source === "manual") {
                        const fmts = chFormats(c.id);
                        return (
                          <td key={`r${c.id}`} className="td text-right">
                            <div className="space-y-1.5">
                              {fmts.map((size) => {
                                const val = manualVal(c.id, vKey(s.skuId, size));
                                const rec2 = channelRestock(val.inv, val.sold, dist.rebalanceLeadDays, dist.daysToSendDays);
                                return (
                                  <div key={size} className="flex items-center justify-end gap-1">
                                    {fmts.length > 1 && <span className="text-[9px] text-muted">{fmtLabel(size)}</span>}
                                    {val.sold > 0 ? (rec2.restock ? <Badge tone="warn">+{num(rec2.byHowMuch)}</Badge> : <Badge tone="ok">OK</Badge>) : <span className="text-muted text-xs">—</span>}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        );
                      }
                      const cv = cover(s.skuId, c.id);
                      const rec = channelRestock(cv.inv, cv.sold, dist.rebalanceLeadDays, dist.daysToSendDays);
                      return <td key={`r${c.id}`} className="td text-right">{cv.sold > 0 ? (rec.restock ? <Badge tone="warn">+{num(rec.byHowMuch)}</Badge> : <Badge tone="ok">OK</Badge>) : <span className="text-muted text-xs">—</span>}</td>;
                    })}
                    <td className="td text-right">
                      {channels.some((c) => cell(s.skuId, c.id).source === "manual")
                        ? <button className="btn-sm border border-edge hover:bg-paper" onClick={() => setEditSku(s.skuId)}>Edit</button>
                        : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-muted mt-2">Manual figures show on-hand packs · packs sold/month · days — click <span className="font-medium">Edit</span> on a row to change them. A manual channel with multiple pack formats (set in Admin → Channels, e.g. 1, 4, 12) gets a line per format. Synced channels are pulled live.</p>
          </div>
          {editSku && (
            <ManualFiguresModal
              skuName={skuName(editSku)}
              rows={channels.filter((c) => cell(editSku, c.id).source === "manual").flatMap((c) =>
                chFormats(c.id).map((size) => {
                  const key = vKey(editSku, size);
                  const v = manualVal(c.id, key);
                  return { chId: c.id, chName: c.name, size, key, inv: v.inv, sold: v.sold, multi: chFormats(c.id).length > 1 };
                }))}
              saving={saving}
              onClose={() => setEditSku(null)}
              onSave={async (vals) => {
                await mutate((draft) => {
                  draft.business.channelData = draft.business.channelData || {};
                  for (const r of vals) {
                    draft.business.channelData[r.chId] = draft.business.channelData[r.chId] || {};
                    draft.business.channelData[r.chId][r.key] = { invPacks: r.inv, packsMo: r.sold };
                  }
                });
                setEditSku(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

// Edit all of a SKU's manual channel figures (per channel × pack format) in one modal — no inline
// autosaving; local state, commit on Save.
function ManualFiguresModal({ skuName, rows, saving, onClose, onSave }: {
  skuName: string;
  rows: { chId: string; chName: string; size: number; key: string; inv: number; sold: number; multi: boolean }[];
  saving: boolean;
  onClose: () => void;
  onSave: (vals: { chId: string; key: string; inv: number; sold: number }[]) => void;
}) {
  const [state, setState] = useState(() => rows.map((r) => ({ ...r, invStr: r.inv ? String(r.inv) : "", soldStr: r.sold ? String(r.sold) : "" })));
  const upd = (i: number, k: "invStr" | "soldStr", v: string) => setState((s) => s.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <Modal title={`${skuName} — channel figures`} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-2 items-center text-sm">
          <div className="text-[11px] uppercase tracking-wide text-muted">Channel</div>
          <div className="text-[11px] uppercase tracking-wide text-muted text-right w-20 sm:w-28">On-hand packs</div>
          <div className="text-[11px] uppercase tracking-wide text-muted text-right w-20 sm:w-28">Packs sold / mo</div>
          {state.map((r, i) => (
            <Fragment key={r.chId + r.size}>
              <div className="font-medium min-w-0 truncate">{r.chName}{r.multi && <span className="text-muted font-normal ml-1">· {r.size === 1 ? "single" : `${r.size}pk`}</span>}</div>
              <input type="number" step="any" className="input py-1 text-right w-20 sm:w-28" value={r.invStr} onChange={(e) => upd(i, "invStr", e.target.value)} />
              <input type="number" step="any" className="input py-1 text-right w-20 sm:w-28" value={r.soldStr} onChange={(e) => upd(i, "soldStr", e.target.value)} />
            </Fragment>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={saving} onClick={() => onSave(state.map((r) => ({ chId: r.chId, key: r.key, inv: Number(r.invStr) || 0, sold: Number(r.soldStr) || 0 })))}>{saving ? "…" : "Save"}</button>
        </div>
      </div>
    </Modal>
  );
}
